import { Badge } from "@/components/ui/badge"
import { CheckCircle2, AlertCircle, HelpCircle } from "lucide-react"
import { cn } from "@/lib/utils"

type ConfidenceLevel = "high" | "medium" | "low" | "none"

interface ConfidenceBadgeProps {
  level: ConfidenceLevel
  reviewCount: number
  className?: string
  showIcon?: boolean
  showText?: boolean
}

export function ConfidenceBadge({
  level,
  reviewCount,
  className,
  showIcon = true,
  showText = true,
}: ConfidenceBadgeProps) {
  const config = {
    high: {
      color:
        "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
      icon: CheckCircle2,
      text: "High confidence",
      description: `Based on ${reviewCount} reviews`,
    },
    medium: {
      color:
        "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
      icon: AlertCircle,
      text: "Medium confidence",
      description: `Based on ${reviewCount} reviews`,
    },
    low: {
      color: "bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800",
      icon: HelpCircle,
      text: "Low confidence",
      description: `Only ${reviewCount} review${reviewCount === 1 ? "" : "s"}`,
    },
    none: {
      color: "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800",
      icon: HelpCircle,
      text: "No data",
      description: "No reviews yet",
    },
  }

  const { color, icon: Icon, text, description } = config[level]

  return (
    <Badge variant="outline" className={cn(color, "text-xs font-medium", className)} title={description}>
      {showIcon && <Icon className="w-3 h-3 mr-1" />}
      {showText && text}
      {!showText && reviewCount > 0 && ` (${reviewCount})`}
    </Badge>
  )
}
